import { useCallback, useMemo, useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../config/api'
import {
  artistKey,
  buildSlots,
  findConflicts,
  groupSlotsByDay,
  resolveLineup,
  resolveStages,
  toDayKey
} from '../utils/scheduleUtils'

/**
 * Schedule planner state.
 *
 * Two different writes live here, and they go to two different places on
 * purpose:
 *   picks (which sets I'm attending) -> PUT /api/events/:id  (personal)
 *   set times / stages               -> PUT /api/events/:id/lineup (shared)
 *
 * Picks are applied optimistically. Toggling a set is the core interaction and
 * a round trip per tap would make the timeline feel broken; on failure the
 * previous picks are restored and the error surfaced.
 */
export const useSchedulePlanner = (eventId, event, setEvent) => {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const picks = useMemo(() => (Array.isArray(event?.schedule) ? event.schedule : []), [event])

  const slots = useMemo(() => buildSlots(event, picks), [event, picks])
  const days = useMemo(() => groupSlotsByDay(slots), [slots])
  const conflicts = useMemo(() => findConflicts(slots), [slots])
  const untimedSlots = useMemo(() => slots.filter(slot => !slot.hasTime), [slots])

  const flash = useCallback((message) => {
    setSuccess(message)
    setTimeout(() => setSuccess(''), 3000)
  }, [])

  const clearError = useCallback(() => {
    setTimeout(() => setError(''), 5000)
  }, [])

  /** Persists a new picks array, rolling back the optimistic update on failure. */
  const savePicks = useCallback(async (nextPicks) => {
    const previous = picks
    setEvent(current => ({ ...current, schedule: nextPicks }))
    setError('')

    try {
      const response = await axios.put(`${API_BASE_URL}/api/events/${eventId}`, {
        schedule: nextPicks
      }, { withCredentials: true })
      setEvent(response.data)
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving schedule:', err)
      setEvent(current => ({ ...current, schedule: previous }))
      setError(err.response?.status === 403
        ? 'You can only plan your own events'
        : 'Failed to save your schedule')
      clearError()
    }
  }, [eventId, picks, setEvent, clearError])

  /** Adds or removes a set from the plan. */
  const togglePick = useCallback((slot) => {
    const isPicked = picks.some(pick =>
      artistKey(pick.artistName) === slot.key && toDayKey(pick.day) === slot.dayKey
    )

    const nextPicks = isPicked
      ? picks.filter(pick => !(
          artistKey(pick.artistName) === slot.key && toDayKey(pick.day) === slot.dayKey
        ))
      : [...picks, {
          artistName: slot.name,
          day: slot.dayKey || null,
          startTime: slot.startTime,
          endTime: slot.endTime,
          stage: slot.stage,
          priority: 'must',
          note: ''
        }]

    return savePicks(nextPicks)
  }, [picks, savePicks])

  /** Flips a pick between "must see" and "maybe", which softens its conflicts. */
  const cyclePriority = useCallback((slot) => {
    const nextPicks = picks.map(pick => {
      if (artistKey(pick.artistName) !== slot.key || toDayKey(pick.day) !== slot.dayKey) return pick
      return { ...pick, priority: pick.priority === 'must' ? 'maybe' : 'must' }
    })
    return savePicks(nextPicks)
  }, [picks, savePicks])

  /**
   * Writes set times back to the SHARED lineup.
   *
   * The whole lineup is sent, not a patch: the server rebuilds officialLineup
   * from the payload, which keeps sanitization in one place and avoids
   * per-artist index arithmetic against a list a flyer update may have
   * reordered.
   */
  const saveSetTimes = useCallback(async (edits) => {
    setSaving(true)
    setError('')

    const lineup = resolveLineup(event).map(artist => {
      const edit = edits[artistKey(artist.name)]
      return {
        name: artist.name,
        genre: artist.genre || '',
        performanceDate: edit?.performanceDate || artist.performanceDate || null,
        startTime: edit ? edit.startTime : (artist.startTime || ''),
        endTime: edit ? edit.endTime : (artist.endTime || ''),
        stage: edit ? edit.stage : (artist.stage || ''),
        setTime: artist.setTime || ''
      }
    })

    try {
      const response = await axios.put(`${API_BASE_URL}/api/events/${eventId}/lineup`, {
        officialLineup: lineup
      }, { withCredentials: true })

      // The endpoint answers with just the shared lineup, so merge it into the
      // event rather than replacing the object and dropping personal fields.
      setEvent(current => ({ ...current, officialLineup: response.data.officialLineup }))

      flash('Set times saved')
      return true
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving set times:', err)
      setError(err.response?.data?.message || 'Failed to save set times')
      clearError()
      return false
    } finally {
      setSaving(false)
    }
  }, [event, eventId, setEvent, flash, clearError])

  /**
   * Saves the stage list (shared) without touching set times.
   *
   * Sends the current lineup unchanged alongside it because the endpoint takes
   * both together — the alternative is a second route that can disagree with
   * this one about how a lineup is sanitized.
   */
  const saveStages = useCallback(async (nextStages) => {
    setSaving(true)
    setError('')

    const lineup = resolveLineup(event).map(artist => ({
      name: artist.name,
      genre: artist.genre || '',
      performanceDate: artist.performanceDate || null,
      startTime: artist.startTime || '',
      endTime: artist.endTime || '',
      stage: artist.stage || '',
      setTime: artist.setTime || ''
    }))

    try {
      const response = await axios.put(`${API_BASE_URL}/api/events/${eventId}/lineup`, {
        officialLineup: lineup,
        stages: nextStages
      }, { withCredentials: true })

      setEvent(current => ({
        ...current,
        officialLineup: response.data.officialLineup,
        stages: response.data.stages
      }))

      flash('Stages saved')
      return true
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving stages:', err)
      setError(err.response?.data?.message || 'Failed to save stages')
      clearError()
      return false
    } finally {
      setSaving(false)
    }
  }, [event, eventId, setEvent, flash, clearError])

  const stages = useMemo(() => resolveStages(event, slots), [event, slots])

  return {
    slots,
    days,
    stages,
    conflicts,
    untimedSlots,
    picks,
    saving,
    error,
    success,
    togglePick,
    cyclePriority,
    saveSetTimes,
    saveStages
  }
}
